import React, { forwardRef } from "react";
import { fmtCompact } from "@/lib/format";
import { stageColor } from "@/lib/stages";
import { cn } from "@/lib/utils";

/**
 * One pipeline column: coloured hairline, label + count, sum + probability, scrolling body.
 * Presentational - the board passes droppable props/ref through `...rest`.
 */
export const StageColumn = forwardRef(function StageColumn({ stage, count, sum, focused, draggingOver, children, className, bodyClassName, ...rest }, ref) {
  return (
    <div
      ref={ref}
      {...rest}
      data-testid={`pipeline-stage-column-${stage.key}`}
      className={cn(
        "flex max-h-full w-[292px] shrink-0 flex-col overflow-hidden rounded-2xl border border-black/[0.06] bg-[hsl(40_10%_96.2%)] transition-[border-color,background-color,box-shadow]",
        draggingOver && "border-primary/40 bg-[hsl(10_76%_61%/0.05)]",
        focused && "ring-2 ring-primary/30",
        className
      )}
    >
      <div className="h-[3px] w-full" style={{ backgroundColor: stageColor(stage.key) }} />
      <div className="flex items-center justify-between px-3.5 pb-2 pt-3">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-semibold tracking-[-0.01em]">{stage.label}</span>
          <span className="rounded-full bg-black/[0.06] px-1.5 py-px text-[11px] font-semibold tabular text-foreground/70">{count}</span>
        </div>
        <div className="text-right leading-tight">
          <div className="text-[12.5px] font-semibold tabular" data-num="">{sum ? fmtCompact(sum) : "–"}</div>
          {stage.probability !== undefined && !["won", "lost"].includes(stage.key) ? <div className="text-[10.5px] tabular text-muted-foreground">{stage.probability}% prob.</div> : null}
        </div>
      </div>
      <div className={cn("min-h-[140px] flex-1 overflow-y-auto px-2 pb-2 scrollbar-thin", bodyClassName)}>{children}</div>
    </div>
  );
});

export function StageEmpty({ label }) {
  return <div className="mx-1 mt-1 rounded-xl border border-dashed border-black/[0.1] px-2 py-8 text-center text-[12px] text-muted-foreground/80">Nothing in {String(label || "").toLowerCase()}</div>;
}
